/* eslint-disable @typescript-eslint/no-explicit-any */
import { type Location } from 'react-router'
import { type lngShort, seoLanguageMap } from '../i18n.utils'

interface MetaProps {
	title?: string
	description?: string
	image?: string
	root?: boolean
	robots?: string
	structuredData?: any
	location?: Location
	canonical?: lngShort
	country?: string
	isPublic?: boolean
	isHomepage?: boolean
}

// Define the link type for better type safety
interface LinkMeta {
	rel: string
	tagName: string
	hrefLang?: string
	href: string
}

export const SetMeta = ({
	title,
	description,
	image,
	structuredData,
	robots = 'all',
	root = false,
	location = undefined,
	canonical = 'sv',
	country = 'SE',
	isPublic = false,
	isHomepage = false,
}: MetaProps) => {
	const baseUrl = 'https://evently.se'
	let path = location?.pathname || ''
	if (path === '/') path = ''

	const lngPrefixes: lngShort[] = ['sv', 'en', 'no', 'da', 'fi']
	const pathParts = path.split('/')
	if (lngPrefixes.includes(pathParts[1] as lngShort)) {
		pathParts.splice(1, 1)
		path = pathParts.join('/')
	}

	// Remove date from canonical URL if it exists
	let canonicalPath = path
	if (path.includes('/events/')) {
		const segments = path.split('/')
		const lastSegment = segments[segments.length - 1]
		if (/^\d{6}-\d{4}$/.test(lastSegment)) {
			segments.pop()
			canonicalPath = segments.join('/')
		}
	}

	const seoImage = image ? baseUrl + image : baseUrl + '/img/evently-start.png'

	const formattedTitle = title ? title : 'Error'
	const formattedDescription = description
		? formatDescription(description, 200)
		: ''
	const countryToLang: { [key: string]: lngShort } = {
		SE: 'sv',
		NO: 'no',
		DK: 'da',
		FI: 'fi',
		US: 'en',
	}

	const canonicalLang = country
		? countryToLang[country.toUpperCase()] || canonical
		: canonical

	const { alternateLinks, canonicalLink } = generateAlternateAndCanonicalLinks({
		baseUrl,
		path,
		canonicalPath,
		canonical,
		canonicalLang,
		lngPrefixes,
		country,
		isHomepage,
		isPublic,
	})

	return [
		{ title: formattedTitle + (!root ? ' | Evently' : '') },
		{
			name: 'description',
			property: 'og:description',
			content: formattedDescription,
		},
		{ name: 'application-name', content: 'Evently' },

		{ name: 'og:title', content: formattedTitle },
		{ name: 'og:image', content: seoImage },
		{ name: 'og:type', content: 'website' },
		{ name: 'og:site_name', content: 'Evently' },

		{ name: 'twitter:card', content: 'summary_large_image' },
		{ name: 'twitter:title', content: formattedTitle },
		{ name: 'twitter:description', content: formattedDescription },
		{ name: 'twitter:image', content: seoImage },
		{ name: 'twitter:domain', content: 'evently.se' },
		{ name: 'robots', content: robots },
		...alternateLinks,
		...(canonicalLink ? [canonicalLink] : []),

		...(structuredData ? [structuredData] : []),
	]
}

export function formatDescription(description: string, maxLength: number) {
	if (
		!description ||
		description ===
			'<p class="text-body-md mb-2">För mer information, besök evenemangets hemsida.</p>' ||
		description === '<p class="text-body-md mb-2"></p>'
	) {
		return ''
	}
	const desc = description
		.replace(/(<([^>]+)>)/gi, '')
		.replace(/&nbsp;/g, ' ')
		.replace(/\n/g, ' ')
	if (desc.length > maxLength) {
		let lastIndex = maxLength
		while (
			lastIndex >= 0 &&
			(desc.charAt(lastIndex) !== ' ' || desc.charCodeAt(lastIndex) > 255)
		) {
			lastIndex--
		}

		if (lastIndex > 0 && desc.charAt(lastIndex) === ' ') {
			return desc.substring(0, lastIndex) + '...'
		} else {
			return desc.substring(0, maxLength) + '...'
		}
	}
	return desc
}

export const generateAlternateLinks = (
	baseUrl: string,
	path: string,
	canonicalLang: lngShort,
	includeXDefault: boolean = true,
	countryCode?: string,
) => {
	const lngPrefixes: lngShort[] = ['sv', 'en', 'no', 'da', 'fi']
	const { alternateLinks } = generateAlternateAndCanonicalLinks({
		baseUrl,
		path,
		canonicalPath: path,
		canonical: canonicalLang,
		canonicalLang,
		lngPrefixes,
		country: countryCode,
		isPublic: false,
		isHomepage: false,
	})

	let result = [...alternateLinks]
	if (includeXDefault && !result.some(link => link.hrefLang === 'x-default')) {
		result.push({
			rel: 'alternate',
			tagName: 'link',
			hrefLang: 'x-default',
			href: `${baseUrl}${canonicalLang === 'sv' ? '' : `/${canonicalLang}`}${path}`,
		})
	}

	return result
}

export const generateCanonicalUrl = (
	baseUrl: string,
	path: string,
	canonicalLang: lngShort,
) => {
	// Remove date from canonical URL if it exists
	let canonicalPath = path
	if (path.includes('/events/')) {
		const segments = path.split('/')
		const lastSegment = segments[segments.length - 1]
		if (/^\d{6}-\d{4}$/.test(lastSegment)) {
			segments.pop()
			canonicalPath = segments.join('/')
		}
	}

	// Use the consolidated function but only return the canonicalLink
	const { canonicalLink } = generateAlternateAndCanonicalLinks({
		baseUrl,
		path,
		canonicalPath,
		canonical: canonicalLang,
		canonicalLang,
		lngPrefixes: ['sv', 'en', 'no', 'da', 'fi'],
		isPublic: true,
	})

	return canonicalLink
}

export const generateAlternateAndCanonicalLinks = ({
	baseUrl,
	path,
	canonicalPath,
	canonical,
	canonicalLang,
	lngPrefixes,
	country,
	isHomepage = false,
	isPublic = false,
}: {
	baseUrl: string
	path: string
	canonicalPath: string
	canonical: lngShort
	canonicalLang: lngShort
	lngPrefixes: lngShort[]
	country?: string
	isHomepage?: boolean
	isPublic?: boolean
}) => {
	let alternateLinks: LinkMeta[] = []
	let canonicalLink: LinkMeta | null = null

	if (isHomepage || isPublic) {
		// Logic for homepage and public pages - simplified alternate links with language codes
		alternateLinks = Object.entries(seoLanguageMap)
			.filter(([_, lang]) => lngPrefixes.includes(lang as lngShort))
			.map(([_, lang]) => ({
				rel: 'alternate',
				tagName: 'link',
				hrefLang: `${lang}`,
				href: `${baseUrl}${lang === 'sv' ? '' : `/${lang}`}${decodeURIComponent(path)}`,
			}))

		canonicalLink = {
			rel: 'canonical',
			tagName: 'link',
			href: `${baseUrl}${canonical === 'sv' ? '' : `/${canonical}`}${decodeURIComponent(canonicalPath)}`,
		}
	} else {
		// Original behavior for non-public pages
		alternateLinks = Object.entries(seoLanguageMap).map(([_, lang]) => ({
			rel: 'alternate',
			tagName: 'link',
			hrefLang: country ? `${lang}-${country.toLowerCase()}` : lang,
			href: `${baseUrl}${lang === 'sv' ? '' : `/${lang}`}${decodeURIComponent(path)}`,
		}))

		if (country) {
			alternateLinks.push({
				rel: 'alternate',
				tagName: 'link',
				hrefLang: 'x-default',
				href: `${baseUrl}${canonicalLang === 'sv' ? '' : `/${canonicalLang}`}${decodeURIComponent(path)}`,
			})

			canonicalLink = {
				rel: 'canonical',
				tagName: 'link',
				href: `${baseUrl}${canonicalLang === 'sv' ? '' : `/${canonicalLang}`}${decodeURIComponent(canonicalPath)}`,
			}
		}
	}

	return { alternateLinks, canonicalLink }
}
